import {
  EMBED_FAILURE_REASONS,
  EMBEDDED_BROWSER_STATUSES,
  resolveEmbedFailure,
  type EmbedFailure,
  type EmbedFailureReason,
  type EmbeddedBrowserStatus,
} from '@/shared-responder-v2-core';

import type {
  EmbedProbe,
  RemoteEmbedGateway,
  RemoteEmbedSession,
} from './shared-responder-v2-media-embed-session.types';

export function normalizeStartUrl(startUrl: string | null): string | null {
  if (startUrl === null) return null;

  const trimmed = startUrl.trim();
  return trimmed === '' ? null : trimmed;
}

export function toEmbedFailureMessage(error: unknown): string | null {
  return error instanceof Error ? error.message : null;
}

export type EmbedAttemptOutcome =
  | { readonly kind: 'iframe' }
  | { readonly kind: 'remote'; readonly session: RemoteEmbedSession }
  | { readonly kind: 'failed'; readonly failure: EmbedFailure };

function toCreateFailedFailure(
  error: unknown,
  isEmbeddable: boolean
): EmbedFailure {
  return (
    resolveEmbedFailure({
      isAborted: false,
      isEmbeddable,
      hasCreateError: true,
      message: toEmbedFailureMessage(error),
    }) ?? {
      reason: EMBED_FAILURE_REASONS.CreateFailed,
      message: toEmbedFailureMessage(error),
    }
  );
}

/**
 * The one decision this whole feature hinges on: try the iframe, and only
 * fall back to the remote browser once the probe has actually ruled it out
 * (or errored, which is treated the same way defensively). Whether an eventual
 * `remote.create` failure reads as "this site refuses framing" or a plain
 * create failure depends on which of those got us here.
 */
export async function resolveEmbedAttempt(
  startUrl: string,
  probe: EmbedProbe,
  remote: RemoteEmbedGateway
): Promise<EmbedAttemptOutcome> {
  let confirmedNotEmbeddable = false;

  try {
    const probeResult = await probe(startUrl);

    if (probeResult.canEmbed) {
      return { kind: 'iframe' };
    }

    confirmedNotEmbeddable = true;
  } catch {
    confirmedNotEmbeddable = false;
  }

  try {
    const session = await remote.create({ startUrl });

    return { kind: 'remote', session };
  } catch (error) {
    return {
      kind: 'failed',
      failure: toCreateFailedFailure(error, !confirmedNotEmbeddable),
    };
  }
}

/**
 * Status alone cannot answer this: an aborted attempt lands on `Error` but its
 * stored url may belong to a question already torn down, so the engine refuses
 * to reload it. Publishing the verdict keeps the host from offering a button
 * that silently does nothing.
 */
export function resolveCanReload(args: {
  readonly isCreating: boolean;
  readonly url: string | null;
  readonly failureReason: EmbedFailureReason | null;
  readonly status: EmbeddedBrowserStatus;
}): boolean {
  const { isCreating, url, failureReason, status } = args;

  if (isCreating || url === null) return false;
  if (failureReason === EMBED_FAILURE_REASONS.Aborted) return false;

  return (
    status === EMBEDDED_BROWSER_STATUSES.Ready ||
    status === EMBEDDED_BROWSER_STATUSES.Error
  );
}
